/** Efficiency is the front door; learner tools remain inside the course. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MotionConfig } from 'motion/react';
import { api } from '../../api';
import { applyLocale } from '../../engine/runtime';
import type { PublicMission } from '../../types';
import { Brand, LinkButton } from '../../ui';
import { Toast, useToast } from '../../ui/toast';
import { useLocale, useStrings } from '../../state/useLocale';
import { useAccount } from '../../state/useAccount';
import { legalHref } from '../../lib/legal';
import { COURSE_HREF, hasRecord } from '../../lib/landing';
import { onward } from '../../lib/entitlement';
import { startCheckout, type Plan } from '../../lib/checkout';
import { onOpenSupport } from '../../lib/dock';
import { fetchOffer, redeemCode, type Offer as OfferData } from '../../lib/offer';
import { track } from '../../lib/events';
import { loadProgress } from '../../progress';
import { efficiencyLandingCopy } from '../../lib/strings/efficiency-landing';
import { efficiencyHeroCopy } from '../../lib/strings/efficiency-hero';
import { ChatDock } from '../chat/ChatDock';
import { ChatDoor } from '../chat/ChatDoor';
import { Corner } from '../Corner';
import { Account } from './Account';
import { Invite } from './Invite';
import { Offer } from './Offer';
import { CourseOverview } from './CourseOverview';
import { SavingsHero } from './SavingsHero';
import { AGENT_LINE_ID } from './AgentLine';
import { WhyVideo } from './WhyVideo';
import { Pedigree } from './Credibility';
import { BloborBand } from '../blobor/BloborBand';
import { PreviewResults } from './PreviewResults';

export function Landing() {
  const s = useStrings();
  const { locale } = useLocale();
  const t = efficiencyLandingCopy[locale];
  const { toast, notify, clear } = useToast();
  const account = useAccount(notify);
  const [missions, setMissions] = useState<PublicMission[] | null>(null);
  const [offer, setOffer] = useState<OfferData | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const [dock, setDock] = useState(false);
  const openDock = useCallback(() => setDock(true), []);
  useEffect(() => onOpenSupport(openDock), [openDock]);
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        await applyLocale(locale);
        const list = await api.listMissions();
        if (live) setMissions(list);
      } catch {
        if (live) window.location.replace('/' + COURSE_HREF);
      }
    })();
    return () => { live = false; };
  }, [locale]);
  useEffect(() => {
    let live = true;
    void fetchOffer().then((o) => { if (live) setOffer(o); });
    return () => { live = false; };
  }, []);
  const [progress] = useState(loadProgress);
  const returning = hasRecord(progress);
  const next = useMemo(() => missions && returning ? onward(progress, missions, null, true).open[0] ?? null : null, [missions, progress, returning]);
  const start = missions?.find((m) => m.track === 'general' && !m.locked) ?? null;
  const target = next ?? (returning ? null : start);
  const startHref = target ? '/?mission=' + encodeURIComponent(target.id) : '/' + COURSE_HREF;
  const handleUnlock = useCallback((plan?: Plan) => {
    void startCheckout(plan === 'month' ? 'month' : 'quarter').then((result) => {
      track('checkout-start', { detail: result });
      if (result === 'anonymous') notify(s.app.toast.signInFirst);
      else if (result === 'unavailable') notify(s.app.toast.paymentsOff, 'error');
      else if (result === 'owned') notify(s.app.toast.checkoutOwned);
      else if (result === 'failed') notify(s.app.toast.checkoutFailed, 'error');
    });
  }, [notify, s]);
  if (!missions) return null;
  return <MotionConfig reducedMotion="user">
    <div className="home landing landing--efficiency">
      <header className="bar">
        <Brand href="/" />
        <nav className="bar-end" aria-label={t.course}>
          <a className="btn btn-quiet" href="#savings">{t.results}</a>
          <a className="btn btn-quiet" href="#course">{t.course}</a>
          <a className="btn btn-quiet" href="#credibility">{efficiencyHeroCopy[locale].credibility}</a>
          {missions.some((m) => m.locked) && offer && offer.price !== null && !redeemed && <a className="btn btn-quiet" href="#pricing">{s.home.landing.pricing}</a>}
          {account.loaded && <Account account={account} />}
          <LinkButton variant="quiet" href="/desk">{{ en: 'Try the free incident lesson', ar: 'جرّب درس الحوادث مجانًا', de: 'Kostenlose Vorfalllektion ausprobieren', ja: '無料のインシデント課題を試す' }[locale]}</LinkButton>
          <LinkButton variant="solid" href={'#' + AGENT_LINE_ID}>{efficiencyHeroCopy[locale].barStart}</LinkButton>
        </nav>
      </header>
      {(returning || (account.loaded && account.user)) && <aside className="resume">
        <p>{returning ? next ? s.home.landing.resumeAt(next.level) : s.home.landing.resumeAny : s.home.landing.signedInAs(account.user!.name)}</p>
        <LinkButton variant="quiet" href={'/' + COURSE_HREF}>{s.home.landing.toCourse}</LinkButton>
      </aside>}
      <main className="efficiency-main">
      {/* The start is a line for the visitor's agent; `startHref`, the level in
          this tab, is the hero's quiet "by hand" link (docs/server-workspace.md, 6). */}
      {/* The hero carries the live savings card; the film follows it, always
          open, then what the course is for the person reading (owner,
          2026-10-31). */}
      <SavingsHero byHandHref={startHref} />
      <WhyVideo />
      <CourseOverview missions={missions} />
      <PreviewResults key={account.user?.id ?? 'guest'} missions={missions} progress={progress} userId={account.user?.id ?? null} upgradeHref={offer?.price ? '#pricing' : '/' + COURSE_HREF} />
      <BloborBand />
      <Pedigree />
      {!redeemed && <Offer missions={missions} account={account} offer={offer} place="landing" onUnlock={handleUnlock} onInvite={() => setInviteOpen(true)} />}
      </main>
      <footer className="foot">
        <span>EngineerOS</span>
        <nav className="foot-legal"><a href={legalHref('terms', locale)}>{s.home.footer.legal}</a><a href={legalHref('privacy', locale)}>{s.home.footer.privacy}</a><a href={legalHref('refunds', locale)}>{s.home.footer.refunds}</a></nav>
      </footer>
    </div>
    <Invite open={inviteOpen} missions={missions} account={account} redeem={redeemCode} onRedeemed={() => setRedeemed(true)} onClose={() => setInviteOpen(false)} />
    <Corner chat={dock ? <ChatDock progress={progress} mission={null} notify={notify} account={account} /> : <ChatDoor onOpen={openDock} />} />
    <Toast toast={toast} onDone={clear} />
  </MotionConfig>;
}
export default Landing;
